import { FeedbackSentiment } from "@/api/types";
import type { LanguageType } from "@/store/types/language";
import { OrderPurchaseType } from "@/store/types/order";

export type TelemetryTier = "operational" | "analytics";
export type ClientErrorSource =
  | "window_error"
  | "unhandled_rejection"
  | "render";
export type OrderStep = "details" | "shipping" | "summary" | "payment";
export type PaymentMethod = "credit_card" | "purchase_order" | "on_account";
export type AddressKind = "billing" | "shipping";
export type FeedbackSource = "confirmation" | "feedback_page";
export type RequestResult = "ok" | "error";
export type SignInResult = "ok" | "invalid" | "error";
export type DetailsBlockedReason =
  | "no_products"
  | "below_minimum"
  | "acknowledgements";
type SentimentName = "thumbs_up" | "thumbs_down";
type NoProperties = Record<never, never>;

export const paymentMethods: Record<OrderPurchaseType, PaymentMethod> = {
  [OrderPurchaseType.Credit]: "credit_card",
  [OrderPurchaseType.PurchaseOrder]: "purchase_order",
  [OrderPurchaseType.Account]: "on_account",
};

export const feedbackSentiments: Record<FeedbackSentiment, SentimentName> = {
  [FeedbackSentiment.ThumbsUp]: "thumbs_up",
  [FeedbackSentiment.ThumbsDown]: "thumbs_down",
};

export const getSignInResult = (status?: number): SignInResult =>
  status != null &&
  status >= 400 &&
  status < 500 &&
  status !== 422 &&
  status !== 429
    ? "invalid"
    : "error";

type ChasePaymentProperties = {
  orderId?: string;
  uid?: string;
  language: LanguageType;
  total: number;
};

type FailedFieldsProperties = {
  failed_fields: string;
  count: number;
};

export type TelemetryEvents = {
  chase_startPayment: ChasePaymentProperties;
  chase_handleTransactionErrors: ChasePaymentProperties & { content: unknown };
  chase_cancelPayment: ChasePaymentProperties & { content: unknown };
  chase_completePayment: ChasePaymentProperties;
  validate_order_failed: {
    orderId?: string;
    uid?: string;
    message: string;
    status?: number;
  };

  api_error: {
    endpoint: string;
    method: string;
    status?: number;
    message: string;
    duration_ms: number;
  };
  rate_limited: { endpoint: string };
  captcha_unavailable: { action: string };
  captcha_rejected: { endpoint: string };
  client_error: {
    message: string;
    source: ClientErrorSource;
    component_stack?: string;
  };
  page_not_found: { path: string; referrer_host?: string };
  video_load_failed: { error_code?: number; network_state?: number };

  entry_selected: { entry: "order" | "emergent_signin" | "indigenous_portal" };
  province_selected: { routed_to: "order" | "contact" };
  province_unsupported: NoProperties;
  contact_link_clicked: NoProperties;

  emergent_email_submitted: { result: RequestResult; status?: number };
  emergent_code_submitted: { result: SignInResult; attempt: number };
  emergent_code_resent: { attempt: number };
  indigenous_community_selected: { community_id: string; activated: boolean };
  indigenous_activate_clicked: { community_id?: string };
  indigenous_signin_submitted: { result: SignInResult; community_id?: string };

  order_step_viewed: { step: OrderStep };
  product_quantity_set: {
    product_id: string;
    quantity: number;
    below_minimum: boolean;
  };
  promo_code_applied: {
    result: "ok" | "not_found" | "error";
    discount_percent?: number;
    code?: string;
  };
  details_continue_blocked: {
    reason: DetailsBlockedReason;
    missing_checkboxes: string;
  };
  details_submitted: {
    product_count: number;
    units: number;
    subtotal: number;
    email_updates_opt_in: boolean;
  };
  extra_units_callout_dismissed: NoProperties;
  distributor_list_opened: NoProperties;
  reference_link_clicked: {
    link: "ontario_statute" | "osha" | "overdose_planning_tool";
  };

  address_lookup_searched: {
    result_count: number;
    duration_ms: number;
    query_length: number;
  };
  address_lookup_selected: { result: RequestResult; duration_ms: number };
  manual_address_toggled: {
    address: AddressKind;
    searches_before_switching: number;
  };
  shipping_validation_failed: FailedFieldsProperties;
  shipping_submitted: {
    same_as_billing: boolean;
    manual_address: boolean;
    save_shipping: boolean;
    save_billing: boolean;
  };

  extra_costs_loaded: {
    result: RequestResult;
    shipping_cost?: number;
    tax?: number;
    duration_ms: number;
  };
  edit_shipping_clicked: NoProperties;
  payment_method_chosen: {
    method: PaymentMethod;
    total_band: string;
    over_review_amount: boolean;
    po_allowed: boolean;
  };
  order_create_failed: {
    status?: number;
    message: string;
    method: PaymentMethod;
  };
  order_created: {
    order_id: string;
    method: PaymentMethod;
    total: number;
    over_review_amount: boolean;
    product_count: number;
    promo_applied: boolean;
  };

  order_request_viewed: { order_id?: string; total: number };
  order_confirmed_viewed: { order_id?: string; method: PaymentMethod };
  confirmation_printed: { order_id?: string };
  feedback_sentiment_selected: {
    sentiment: SentimentName;
    source: FeedbackSource;
  };
  feedback_submitted: {
    sentiment: SentimentName;
    has_comment: boolean;
    source: FeedbackSource;
    result: RequestResult;
  };

  registration_terms_viewed: NoProperties;
  registration_form_started: { seconds_since_terms?: number };
  registration_validation_failed: FailedFieldsProperties;
  registration_form_submitted: {
    organization_types: string;
    activities_count: number;
    has_ownership: boolean;
    has_liens: boolean;
  };
  registration_submitted: { result: RequestResult; status?: number };
  registration_completed: { seconds_since_terms?: number };

  training_video_started: NoProperties;
  training_video_completed: { watch_seconds: number };
  certificate_generated: NoProperties;
  certificate_downloaded: { result: RequestResult; render_duration_ms: number };

  language_changed: { from: LanguageType; to: LanguageType };
  leaving_modal_shown: { destination_host?: string };
  leaving_modal_answered: {
    choice: "continue" | "go_back";
    destination_host?: string;
  };
  funnel_abandoned: {
    last_step: OrderStep;
    seconds_on_step: number;
    products_chosen: boolean;
  };
  admin_redirected: { referrer_host?: string };
};

export type TelemetryEventName = keyof TelemetryEvents;

export const eventTiers: Record<TelemetryEventName, TelemetryTier> = {
  chase_startPayment: "operational",
  chase_handleTransactionErrors: "operational",
  chase_cancelPayment: "operational",
  chase_completePayment: "operational",
  validate_order_failed: "operational",

  api_error: "operational",
  rate_limited: "operational",
  captcha_unavailable: "operational",
  captcha_rejected: "operational",
  client_error: "operational",
  page_not_found: "operational",
  video_load_failed: "operational",

  entry_selected: "analytics",
  province_selected: "analytics",
  province_unsupported: "analytics",
  contact_link_clicked: "analytics",

  emergent_email_submitted: "analytics",
  emergent_code_submitted: "analytics",
  emergent_code_resent: "analytics",
  indigenous_community_selected: "analytics",
  indigenous_activate_clicked: "analytics",
  indigenous_signin_submitted: "analytics",

  order_step_viewed: "analytics",
  product_quantity_set: "analytics",
  promo_code_applied: "analytics",
  details_continue_blocked: "analytics",
  details_submitted: "analytics",
  extra_units_callout_dismissed: "analytics",
  distributor_list_opened: "analytics",
  reference_link_clicked: "analytics",

  address_lookup_searched: "analytics",
  address_lookup_selected: "analytics",
  manual_address_toggled: "analytics",
  shipping_validation_failed: "analytics",
  shipping_submitted: "analytics",

  extra_costs_loaded: "analytics",
  edit_shipping_clicked: "analytics",
  payment_method_chosen: "analytics",
  order_create_failed: "analytics",
  order_created: "analytics",

  order_request_viewed: "analytics",
  order_confirmed_viewed: "analytics",
  confirmation_printed: "analytics",
  feedback_sentiment_selected: "analytics",
  feedback_submitted: "analytics",

  registration_terms_viewed: "analytics",
  registration_form_started: "analytics",
  registration_validation_failed: "analytics",
  registration_form_submitted: "analytics",
  registration_submitted: "analytics",
  registration_completed: "analytics",

  training_video_started: "analytics",
  training_video_completed: "analytics",
  certificate_generated: "analytics",
  certificate_downloaded: "analytics",

  language_changed: "analytics",
  leaving_modal_shown: "analytics",
  leaving_modal_answered: "analytics",
  funnel_abandoned: "analytics",
  admin_redirected: "analytics",
};
